import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Scale, FileText, Upload, CheckCircle, XCircle, AlertTriangle, 
  ArrowLeft, RefreshCw, Download, Eye, ChevronRight, ChevronDown,
  BarChart3, FileCheck, Clock, Building2, Loader2, Trash2, Plus
} from 'lucide-react';
import { useBranding } from '../lib/BrandingContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

// Helper function to safely extract error message from API responses
const getErrorMessage = (err, defaultMsg = 'An error occurred') => {
  const detail = err.response?.data?.detail;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors come as array of objects
    return detail.map(e => typeof e === 'string' ? e : (e.msg || JSON.stringify(e))).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return defaultMsg;
};

const LegalOpinionValidation = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, new-validation, cases
  
  // Dashboard stats
  const [stats, setStats] = useState({
    total_cases: 0,
    draft_cases: 0,
    pending_docs: 0,
    validating: 0,
    validated: 0,
    accepted: 0,
    rejected: 0,
    fit_ratio: 0
  });
  
  // Cases list
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  
  // New validation wizard
  const [wizardStep, setWizardStep] = useState(1); // 1=details, 2=tier1, 3=tier2, 4=tier3, 5=review
  const [documentTiers, setDocumentTiers] = useState(null);
  const [caseForm, setCaseForm] = useState({
    bank_name: '',
    branch: '',
    loan_application_no: '',
    applicant_names: '',
    property_type: 'urban',
    state: 'Telangana',
    district: '',
    mandal: '',
    village: '',
    validation_scope: 'both' // chain_only, identity_only, or both
  });
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({
    tier_1: [],
    tier_2: [],
    tier_3: [],
    tier_4: []
  });
  const [uploadingDocType, setUploadingDocType] = useState(null); // Track specific doc type being uploaded
  const [preCheckResult, setPreCheckResult] = useState(null);
  
  // Validation state
  const [validationStatus, setValidationStatus] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/love/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching LOVE stats:', err);
    }
  }, [API_URL]);

  const fetchCases = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/love/cases`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  }, [API_URL]);

  const fetchDocumentTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/love/document-tiers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocumentTiers(response.data);
    } catch (err) {
      console.error('Error fetching document tiers:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    try {
      setUser(JSON.parse(userData));
      Promise.all([fetchStats(), fetchCases(), fetchDocumentTiers()]).finally(() => {
        setLoading(false);
      });
    } catch (err) {
      console.error('Error:', err);
      navigate('/login');
    }
  }, [navigate, fetchStats, fetchCases, fetchDocumentTiers]);

  // Create new case
  const handleCreateCase = async () => {
    if (!caseForm.bank_name || !caseForm.loan_application_no || !caseForm.applicant_names) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const formData = new FormData();
      Object.entries(caseForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await axios.post(`${API_URL}/api/love/cases`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setCurrentCaseId(response.data.case_id);
      toast.success(`Case ${response.data.case_number} created`);
      setWizardStep(2);
    } catch (err) {
      console.error('Error creating case:', err);
      toast.error(getErrorMessage(err, 'Failed to create case'));
    }
  };

  // Upload documents
  // Upload single file to GCS (for large files)
  const uploadFileToGCS = async (file, docType, tier) => {
    const token = localStorage.getItem('token');
    
    // Step 1: Get signed upload URL
    const urlFormData = new FormData();
    urlFormData.append('filename', file.name);
    urlFormData.append('content_type', file.type || 'application/octet-stream');
    urlFormData.append('doc_type', docType);
    urlFormData.append('tier', tier);

    const urlResponse = await axios.post(
      `${API_URL}/api/love/cases/${currentCaseId}/get-upload-url`,
      urlFormData,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const { upload_url, blob_path, gs_uri } = urlResponse.data;

    // Step 2: Upload directly to GCS using XMLHttpRequest for progress
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`GCS upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.ontimeout = () => reject(new Error('Upload timeout'));

      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.timeout = 600000; // 10 minute timeout
      xhr.send(file);
    });

    // Step 3: Confirm upload with backend
    const confirmFormData = new FormData();
    confirmFormData.append('blob_path', blob_path);
    confirmFormData.append('filename', file.name);
    confirmFormData.append('file_size', file.size);
    confirmFormData.append('doc_type', docType);
    confirmFormData.append('tier', tier);
    confirmFormData.append('gs_uri', gs_uri);

    await axios.post(
      `${API_URL}/api/love/cases/${currentCaseId}/confirm-upload`,
      confirmFormData,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return true;
  };

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDocumentUpload = async (event, docType, tier) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!currentCaseId) {
      toast.error('Please create a case first');
      return;
    }

    // Track specific doc type being uploaded (not just tier)
    setUploadingDocType(docType);
    setUploadProgress(0);

    try {
      // Check total file size - use GCS for files > 1MB
      const totalSize = files.reduce((sum, f) => sum + f.size, 0);
      const useGCS = totalSize > 1 * 1024 * 1024; // 1MB threshold

      if (useGCS) {
        // Upload files one by one to GCS
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          toast.info(`Uploading ${file.name} (${i + 1}/${files.length})...`);
          
          try {
            await uploadFileToGCS(file, docType, tier);
          } catch (gcsError) {
            console.error('GCS upload failed, trying direct upload:', gcsError);
            // Fallback to direct upload if GCS fails
            const formData = new FormData();
            formData.append('files', file);
            formData.append('doc_type', docType);
            formData.append('tier', tier);
            
            await axios.post(
              `${API_URL}/api/love/cases/${currentCaseId}/documents`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
          }
        }
      } else {
        // Small files - use direct upload
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        formData.append('doc_type', docType);
        formData.append('tier', tier);

        await axios.post(
          `${API_URL}/api/love/cases/${currentCaseId}/documents`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
      }

      // Refresh case documents from backend to ensure state is in sync
      await refreshCaseDocuments(currentCaseId);

      toast.success(`Uploaded ${files.length} document(s)`);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(getErrorMessage(err, 'Upload failed. Try smaller files or check your connection.'));
    } finally {
      setUploadingDocType(null);
      setUploadProgress(0);
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId, tier) => {
    if (!currentCaseId) return;

    try {
      await axios.delete(`${API_URL}/api/love/cases/${currentCaseId}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Refresh from backend to ensure state is in sync
      await refreshCaseDocuments(currentCaseId);

      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  // Run pre-check
  const handlePreCheck = async () => {
    if (!currentCaseId) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/love/cases/${currentCaseId}/documents/pre-check`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setPreCheckResult(response.data);
      
      if (response.data.can_validate) {
        toast.success('All mandatory documents uploaded. Ready for validation!');
      } else {
        toast.warning(`Missing mandatory documents: ${response.data.missing_tier1.join(', ')}`);
      }
    } catch (err) {
      toast.error('Pre-check failed');
    }
  };

  // Run validation
  const handleRunValidation = async () => {
    if (!currentCaseId) return;

    setIsValidating(true);
    setValidationStatus({ status: 'starting', progress_percent: 0 });

    try {
      const response = await axios.post(
        `${API_URL}/api/love/cases/${currentCaseId}/validate`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const { job_id } = response.data;
      toast.info('Validation started. This may take a few minutes...');

      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API_URL}/api/love/cases/${currentCaseId}/status`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );

          setValidationStatus(statusRes.data);

          if (statusRes.data.status === 'completed') {
            clearInterval(pollInterval);
            setIsValidating(false);
            toast.success('Validation complete!');
            fetchCases();
            handleViewCase(currentCaseId);
          } else if (statusRes.data.status === 'error') {
            clearInterval(pollInterval);
            setIsValidating(false);
            toast.error(statusRes.data.error_message || 'Validation failed');
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 3000);

    } catch (err) {
      setIsValidating(false);
      toast.error(getErrorMessage(err, 'Failed to start validation'));
    }
  };

  // Helper: Populate uploadedDocs state from case documents
  const populateUploadedDocsFromCase = (caseData) => {
    if (!caseData || !caseData.documents) return;
    
    const docsByTier = {
      tier_1: [],
      tier_2: [],
      tier_3: [],
      tier_4: []
    };
    
    caseData.documents.forEach(doc => {
      const tierKey = `tier_${doc.tier}`;
      if (docsByTier[tierKey]) {
        docsByTier[tierKey].push({
          doc_id: doc.doc_id,
          filename: doc.filename,
          doc_type: doc.doc_type,
          tier: doc.tier
        });
      }
    });
    
    setUploadedDocs(docsByTier);
  };

  // View case
  const handleViewCase = async (caseId) => {
    try {
      const response = await axios.get(`${API_URL}/api/love/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedCase(response.data);
      // Also populate uploadedDocs for consistency
      populateUploadedDocsFromCase(response.data);
      setActiveTab('cases');
    } catch (err) {
      toast.error('Failed to load case');
    }
  };
  
  // Fetch case and update uploadedDocs (for wizard use)
  const refreshCaseDocuments = async (caseId) => {
    try {
      const response = await axios.get(`${API_URL}/api/love/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      populateUploadedDocsFromCase(response.data);
    } catch (err) {
      console.error('Failed to refresh case documents:', err);
    }
  };

  // Parse opinion with AI
  const [parsedEntities, setParsedEntities] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState(null);
  
  const handleParseOpinion = async (caseId) => {
    setIsParsing(true);
    setParseProgress({ status: 'starting', progress_percent: 0 });
    
    try {
      // Start the parsing job
      const response = await axios.post(
        `${API_URL}/api/love/cases/${caseId}/parse-opinion`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.info('Opinion parsing started. This may take a minute...');
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API_URL}/api/love/cases/${caseId}/parse-status`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          setParseProgress(statusRes.data);
          
          if (statusRes.data.status === 'completed') {
            clearInterval(pollInterval);
            setIsParsing(false);
            setParsedEntities(statusRes.data.result);
            toast.success('Opinion parsed successfully');
          } else if (statusRes.data.status === 'error') {
            clearInterval(pollInterval);
            setIsParsing(false);
            toast.error(statusRes.data.error_message || 'Parsing failed');
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
          // Continue polling on transient errors
        }
      }, 3000);
      
      // Timeout after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isParsing) {
          setIsParsing(false);
          toast.error('Parsing timed out. Please try again.');
        }
      }, 180000);
      
    } catch (err) {
      setIsParsing(false);
      toast.error(getErrorMessage(err, 'Failed to start parsing'));
    }
  };

  // Download report
  const handleDownloadReport = async (caseId, format = 'pdf') => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      const response = await axios.get(
        `${API_URL}/api/love/cases/${caseId}/report?format=${format}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const extension = format === 'pdf' ? 'pdf' : 'docx';
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `validation_report_${caseId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  // Human decision actions
  const handleMarkFit = async (caseId, remarks) => {
    try {
      const formData = new FormData();
      formData.append('remarks', remarks);

      await axios.post(
        `${API_URL}/api/love/cases/${caseId}/actions/mark-fit-for-processing`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Case marked as Fit for Processing');
      fetchCases();
      if (selectedCase?._id === caseId) {
        handleViewCase(caseId);
      }
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const handleMarkNotFit = async (caseId, remarks) => {
    try {
      const formData = new FormData();
      formData.append('remarks', remarks);

      await axios.post(
        `${API_URL}/api/love/cases/${caseId}/actions/mark-not-fit-for-processing`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Case marked as Not Fit for Processing');
      fetchCases();
      if (selectedCase?._id === caseId) {
        handleViewCase(caseId);
      }
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const handleRevertToAdvocate = async (caseId, remarks) => {
    try {
      const formData = new FormData();
      formData.append('remarks', remarks);

      await axios.post(
        `${API_URL}/api/love/cases/${caseId}/actions/revert-to-advocate`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Case reverted to Advocate');
      fetchCases();
      if (selectedCase?._id === caseId) {
        handleViewCase(caseId);
      }
    } catch (err) {
      toast.error('Action failed');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="bg-gray-100 text-gray-800">Draft</Badge>,
      documents_pending: <Badge className="bg-yellow-100 text-yellow-800">Docs Pending</Badge>,
      ready_for_validation: <Badge className="bg-blue-100 text-blue-800">Ready</Badge>,
      validating: <Badge className="bg-purple-100 text-purple-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Validating</Badge>,
      validated: <Badge className="bg-cyan-100 text-cyan-800">Validated</Badge>,
      accepted: <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Fit</Badge>,
      rejected: <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Not Fit</Badge>,
      reverted_to_advocate: <Badge className="bg-orange-100 text-orange-800">Reverted</Badge>,
      error: <Badge className="bg-red-100 text-red-800">Error</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  // Get recommendation badge
  const getRecommendationBadge = (rec) => {
    const badges = {
      fit: <Badge className="bg-green-600 text-white">FIT FOR PROCESSING</Badge>,
      fit_with_conditions: <Badge className="bg-yellow-600 text-white">FIT WITH CONDITIONS</Badge>,
      not_fit: <Badge className="bg-red-600 text-white">NOT FIT – REVERT</Badge>
    };
    return badges[rec] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading LOVE Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-2">
                <Scale className="w-6 h-6 text-emerald-600" />
                <span className="text-xl font-bold text-gray-900">Legal Opinion Validation</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchCases(); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'new-validation', label: 'New Validation', icon: Plus },
              { id: 'cases', label: 'Validation Cases', icon: FileCheck }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { 
                  setActiveTab(tab.id); 
                  if (tab.id === 'new-validation') {
                    setWizardStep(1);
                    setCurrentCaseId(null);
                    setUploadedDocs({ tier_1: [], tier_2: [], tier_3: [], tier_4: [] });
                    setPreCheckResult(null);
                    setCaseForm({
                      bank_name: '',
                      branch: '',
                      loan_application_no: '',
                      applicant_names: '',
                      property_type: 'urban',
                      state: 'Telangana',
                      district: '',
                      mandal: '',
                      village: '',
                      validation_scope: 'both'
                    });
                  }
                }}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`love-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Cases</p>
                      <p className="text-3xl font-bold text-gray-900">{stats.total_cases}</p>
                    </div>
                    <FileText className="w-10 h-10 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Pending Docs</p>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pending_docs}</p>
                    </div>
                    <Upload className="w-10 h-10 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Validated</p>
                      <p className="text-3xl font-bold text-cyan-600">{stats.validated}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-cyan-400" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Fit Ratio</p>
                      <p className="text-3xl font-bold text-green-600">{stats.fit_ratio.toFixed(0)}%</p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4">
                  <Button 
                    onClick={() => setActiveTab('new-validation')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="start-new-validation"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Start New Validation
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('cases')}>
                    <Eye className="w-4 h-4 mr-2" />
                    View All Cases
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Cases */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Validation Cases</CardTitle>
              </CardHeader>
              <CardContent>
                {cases.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No validation cases yet. Start a new validation!</p>
                ) : (
                  <div className="space-y-3">
                    {cases.slice(0, 5).map(c => (
                      <div key={c._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer" onClick={() => handleViewCase(c._id)}>
                        <div className="flex items-center space-x-4">
                          <Building2 className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{c.case_number}</p>
                            <p className="text-sm text-gray-500">{c.bank_name} • {c.applicant_names?.join(', ')}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {c.validation_score && (
                            <span className="text-lg font-semibold text-gray-700">{c.validation_score}/100</span>
                          )}
                          {getStatusBadge(c.status)}
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'new-validation' && (
          <div className="max-w-4xl mx-auto">
            {/* Wizard Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {['Case Details', 'Tier-1 (Mandatory)', 'Tier-2 (Bank)', 'Tier-3 (Conditional)', 'Review & Validate'].map((step, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      wizardStep > idx + 1 ? 'bg-emerald-600 text-white' :
                      wizardStep === idx + 1 ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {wizardStep > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                    </div>
                    <span className={`ml-2 text-sm ${wizardStep === idx + 1 ? 'text-emerald-700 font-medium' : 'text-gray-500'}`}>
                      {step}
                    </span>
                    {idx < 4 && <div className="w-8 h-px bg-gray-300 mx-2" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Step 1: Case Details */}
            {wizardStep === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Case Details</CardTitle>
                  <CardDescription>Enter bank and property information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                      <input
                        type="text"
                        value={caseForm.bank_name}
                        onChange={e => setCaseForm({ ...caseForm, bank_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., State Bank of India"
                        data-testid="love-bank-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                      <input
                        type="text"
                        value={caseForm.branch}
                        onChange={e => setCaseForm({ ...caseForm, branch: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., Jubilee Hills"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Loan Application No *</label>
                      <input
                        type="text"
                        value={caseForm.loan_application_no}
                        onChange={e => setCaseForm({ ...caseForm, loan_application_no: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., HL/2025/12345"
                        data-testid="love-loan-no"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Applicant Name(s) *</label>
                      <input
                        type="text"
                        value={caseForm.applicant_names}
                        onChange={e => setCaseForm({ ...caseForm, applicant_names: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., John Doe, Jane Doe"
                        data-testid="love-applicants"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                      <select
                        value={caseForm.property_type}
                        onChange={e => setCaseForm({ ...caseForm, property_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="urban">Urban</option>
                        <option value="rural">Rural</option>
                        <option value="agricultural">Agricultural</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={caseForm.state}
                        onChange={e => setCaseForm({ ...caseForm, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="Telangana">Telangana</option>
                        <option value="Andhra Pradesh">Andhra Pradesh</option>
                        <option value="Karnataka">Karnataka</option>
                        <option value="Tamil Nadu">Tamil Nadu</option>
                        <option value="Maharashtra">Maharashtra</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                      <input
                        type="text"
                        value={caseForm.district}
                        onChange={e => setCaseForm({ ...caseForm, district: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., Hyderabad"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Village/Location</label>
                      <input
                        type="text"
                        value={caseForm.village}
                        onChange={e => setCaseForm({ ...caseForm, village: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g., Gachibowli"
                      />
                    </div>
                  </div>
                  
                  {/* Validation Scope - Controls mandatory documents */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validation Scope *</label>
                    <p className="text-xs text-gray-500 mb-3">Select which document categories should be mandatory for this validation</p>
                    <div className="space-y-3">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={caseForm.validation_scope === 'chain_only' || caseForm.validation_scope === 'both'}
                          onChange={e => {
                            const chainChecked = e.target.checked;
                            const identityChecked = caseForm.validation_scope === 'identity_only' || caseForm.validation_scope === 'both';
                            if (chainChecked && identityChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'both' });
                            } else if (chainChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'chain_only' });
                            } else if (identityChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'identity_only' });
                            } else {
                              setCaseForm({ ...caseForm, validation_scope: 'chain_only' }); // At least one must be selected
                            }
                          }}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Chain of Title Documents</span>
                          <p className="text-xs text-gray-500">Legal Opinion, Title Deeds, Encumbrance Certificate - validates ownership flow and transfer history</p>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={caseForm.validation_scope === 'identity_only' || caseForm.validation_scope === 'both' || caseForm.validation_scope === 'chain_plus_identity'}
                          onChange={e => {
                            const identityChecked = e.target.checked;
                            const chainChecked = caseForm.validation_scope === 'chain_only' || caseForm.validation_scope === 'both';
                            if (chainChecked && identityChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'both' });
                            } else if (identityChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'identity_only' });
                            } else if (chainChecked) {
                              setCaseForm({ ...caseForm, validation_scope: 'chain_only' });
                            } else {
                              setCaseForm({ ...caseForm, validation_scope: 'chain_only' }); // At least one must be selected
                            }
                          }}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Property Identity Documents</span>
                          <p className="text-xs text-gray-500">Patta/RTC/RoR documents - validates property identity and land records consistency</p>
                        </div>
                      </label>
                    </div>
                    <div className="mt-3 text-xs text-blue-700 bg-blue-100 px-3 py-2 rounded">
                      {caseForm.validation_scope === 'both' || caseForm.validation_scope === 'chain_plus_identity' ? (
                        <span>✓ Full validation: Both Chain of Title and Property Identity documents will be mandatory</span>
                      ) : caseForm.validation_scope === 'chain_only' ? (
                        <span>✓ Chain of Title only: Property Identity documents (Patta/RTC/RoR) will be optional</span>
                      ) : caseForm.validation_scope === 'identity_only' ? (
                        <span>✓ Property Identity only: Chain documents will still be validated if provided</span>
                      ) : null}
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleCreateCase} className="bg-emerald-600 hover:bg-emerald-700" data-testid="love-create-case">
                      Create Case & Continue
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2-4: Document Upload Tiers */}
            {wizardStep >= 2 && wizardStep <= 4 && documentTiers && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {wizardStep === 2 && 'Tier-1: Mandatory Documents'}
                    {wizardStep === 3 && 'Tier-2: Bank-Mandatory Documents'}
                    {wizardStep === 4 && 'Tier-3: Conditional Documents'}
                  </CardTitle>
                  <CardDescription>
                    {wizardStep === 2 && (
                      <span>
                        These documents are required to run validation.
                        {caseForm.validation_scope === 'chain_only' && (
                          <span className="ml-2 text-blue-600">(Chain-only: Property Identity optional)</span>
                        )}
                        {caseForm.validation_scope === 'identity_only' && (
                          <span className="ml-2 text-blue-600">(Identity-only: Chain documents optional)</span>
                        )}
                        {(caseForm.validation_scope === 'both' || caseForm.validation_scope === 'chain_plus_identity') && (
                          <span className="ml-2 text-emerald-600">(Full scope: All documents required)</span>
                        )}
                      </span>
                    )}
                    {wizardStep === 3 && 'Bank may require these for processing'}
                    {wizardStep === 4 && 'Upload if applicable to your case'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(documentTiers).filter(([key]) => key === `tier_${wizardStep - 1}`).map(([tierKey, tierData]) => (
                    <div key={tierKey} className="space-y-4">
                      {tierData.documents?.map(doc => {
                        // Dynamic required status based on validation scope
                        const isPropertyIdentity = doc.type === 'property_identity';
                        const isChainDoc = ['legal_opinion', 'title_deeds', 'encumbrance_certificate'].includes(doc.type);
                        
                        // Determine if document is required based on scope
                        let isRequired = doc.required;
                        let isOptionalByScope = false;
                        let optionalReason = '';
                        
                        if (isPropertyIdentity) {
                          // Property identity required only for 'identity_only' or 'both'
                          isRequired = ['identity_only', 'both', 'chain_plus_identity'].includes(caseForm.validation_scope);
                          if (!isRequired) {
                            isOptionalByScope = true;
                            optionalReason = 'Chain-only scope';
                          }
                        } else if (isChainDoc) {
                          // Chain docs required for 'chain_only' or 'both'
                          isRequired = ['chain_only', 'both', 'chain_plus_identity'].includes(caseForm.validation_scope);
                          if (caseForm.validation_scope === 'identity_only') {
                            isOptionalByScope = true;
                            optionalReason = 'Identity-only scope';
                          }
                        }
                        
                        // Get uploaded docs for this type
                        const uploadedForType = uploadedDocs[`tier_${wizardStep - 1}`]?.filter(d => d.doc_type === doc.type) || [];
                        const hasUploads = uploadedForType.length > 0;
                        
                        return (
                          <div key={doc.type} className={`p-4 border rounded-lg ${hasUploads ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                {hasUploads ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <FileText className="w-5 h-5 text-gray-400" />
                                )}
                                <span className="font-medium">{doc.label}</span>
                                {isRequired && (
                                  <Badge variant="outline" className="text-red-600 border-red-200">Required</Badge>
                                )}
                                {isOptionalByScope && (
                                  <Badge variant="outline" className="text-blue-600 border-blue-200">Optional ({optionalReason})</Badge>
                                )}
                                {hasUploads && (
                                  <Badge className="bg-green-600 text-white">{uploadedForType.length} uploaded</Badge>
                                )}
                              </div>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept=".pdf,.docx,.doc"
                                  multiple={doc.multiple}
                                  onChange={e => handleDocumentUpload(e, doc.type, wizardStep - 1)}
                                  className="hidden"
                                  disabled={uploadingDocType === doc.type}
                                />
                                <Button variant="outline" size="sm" asChild disabled={uploadingDocType === doc.type}>
                                  <span>
                                    {uploadingDocType === doc.type ? (
                                      <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading...'}
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {hasUploads ? 'Add More' : 'Upload'}
                                      </>
                                    )}
                                  </span>
                                </Button>
                              </label>
                            </div>
                            {/* Upload progress bar */}
                            {uploadingDocType === doc.type && uploadProgress > 0 && (
                              <div className="mt-2">
                                <Progress value={uploadProgress} className="h-2" />
                                <p className="text-xs text-gray-500 mt-1">Uploading to cloud storage...</p>
                              </div>
                            )}
                            {/* Show uploaded docs for this type */}
                            {uploadedForType.map(uploaded => (
                              <div key={uploaded.doc_id} className="flex items-center justify-between py-2 px-3 bg-white border border-green-200 rounded mt-2">
                                <div className="flex items-center space-x-2">
                                  <FileCheck className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-gray-800">{uploaded.filename}</span>
                                </div>
                                <button onClick={() => handleDeleteDocument(uploaded.doc_id, wizardStep - 1)} className="text-red-500 hover:text-red-700">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button onClick={() => setWizardStep(wizardStep + 1)} className="bg-emerald-600 hover:bg-emerald-700">
                      Continue
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Review & Validate */}
            {wizardStep === 5 && (
              <Card>
                <CardHeader>
                  <CardTitle>Review & Run Validation</CardTitle>
                  <CardDescription>Verify uploaded documents and start validation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Pre-check */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2">Document Pre-Check</h4>
                    {preCheckResult ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {preCheckResult.can_validate ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          )}
                          <span>{preCheckResult.message}</span>
                        </div>
                        <p className="text-sm text-gray-500">Total documents: {preCheckResult.total_documents}</p>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={handlePreCheck}>
                        Run Pre-Check
                      </Button>
                    )}
                  </div>

                  {/* Uploaded Summary */}
                  <div className="space-y-2">
                    <h4 className="font-medium">Uploaded Documents Summary</h4>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="p-2 bg-red-50 rounded text-center">
                        <p className="font-medium">Tier 1</p>
                        <p className="text-2xl font-bold text-red-600">{uploadedDocs.tier_1.length}</p>
                      </div>
                      <div className="p-2 bg-orange-50 rounded text-center">
                        <p className="font-medium">Tier 2</p>
                        <p className="text-2xl font-bold text-orange-600">{uploadedDocs.tier_2.length}</p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded text-center">
                        <p className="font-medium">Tier 3</p>
                        <p className="text-2xl font-bold text-blue-600">{uploadedDocs.tier_3.length}</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded text-center">
                        <p className="font-medium">Tier 4</p>
                        <p className="text-2xl font-bold text-gray-600">{uploadedDocs.tier_4.length}</p>
                      </div>
                    </div>
                  </div>

                  {/* Validation Progress */}
                  {isValidating && validationStatus && (
                    <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                        <span className="font-medium text-purple-700">Validation in Progress</span>
                      </div>
                      <Progress value={validationStatus.progress_percent || 0} className="h-2 mb-2" />
                      <p className="text-sm text-purple-600">{validationStatus.current_step || 'Processing...'}</p>
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setWizardStep(4)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      onClick={handleRunValidation} 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={isValidating || (preCheckResult && !preCheckResult.can_validate)}
                      data-testid="love-run-validation"
                    >
                      {isValidating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <Scale className="w-4 h-4 mr-2" />
                          Run Validation
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'cases' && (
          <div className="space-y-6">
            {selectedCase ? (
              /* Case Detail View */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={() => setSelectedCase(null)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Cases
                  </Button>
                  <div className="flex space-x-2">
                    {selectedCase.validation_result && (
                      <>
                        <Button variant="outline" onClick={() => handleDownloadReport(selectedCase._id, 'pdf')}>
                          <Download className="w-4 h-4 mr-2" />
                          PDF Report
                        </Button>
                        <Button variant="outline" onClick={() => handleDownloadReport(selectedCase._id, 'docx')}>
                          <Download className="w-4 h-4 mr-2" />
                          DOCX Report
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Case Header */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{selectedCase.case_number}</h2>
                        <p className="text-gray-500">{selectedCase.bank_name} • {selectedCase.branch}</p>
                        <p className="text-gray-500">Loan: {selectedCase.loan_application_no}</p>
                        <p className="text-gray-600 mt-2">Applicants: {selectedCase.applicant_names?.join(', ')}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(selectedCase.status)}
                        {selectedCase.validation_result && (
                          <div className="mt-4">
                            <p className="text-4xl font-bold text-gray-900">{selectedCase.validation_result.overall_score}/100</p>
                            <div className="mt-2">
                              {getRecommendationBadge(selectedCase.validation_result.recommendation)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Parse Opinion Button */}
                    {selectedCase.documents?.some(d => d.doc_type === 'legal_opinion') && !parsedEntities && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <Button 
                          onClick={() => handleParseOpinion(selectedCase._id)}
                          variant="outline"
                          className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                          disabled={isParsing}
                          data-testid="parse-opinion-btn"
                        >
                          {isParsing ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing with AI...</>
                          ) : (
                            <><Eye className="w-4 h-4 mr-2" />Parse Opinion with AI</>
                          )}
                        </Button>
                        {isParsing && parseProgress && (
                          <div className="mt-2">
                            <Progress value={parseProgress.progress_percent || 0} className="h-2" />
                            <p className="text-xs text-blue-600 mt-1">{parseProgress.current_step || 'Processing...'}</p>
                          </div>
                        )}
                        {!isParsing && (
                          <p className="text-xs text-gray-500 mt-1">Extract sections, entities, and title chain using Gemini AI</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Parsed Entities (AI Extraction Results) */}
                {parsedEntities && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Eye className="w-5 h-5 text-blue-600" />
                        <span>AI-Extracted Information</span>
                      </CardTitle>
                      <CardDescription>Sections and entities extracted from the legal opinion using Gemini</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Sections Found */}
                      <div>
                        <h4 className="font-medium mb-3">Opinion Sections</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(parsedEntities.sections || {}).map(([key, section]) => (
                            <div key={key} className={`p-2 rounded text-center text-sm ${
                              section.found ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {section.found ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}
                              {key.replace(/_/g, ' ')}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Survey Numbers */}
                      {parsedEntities.entities?.survey_numbers?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Survey Numbers</h4>
                          <div className="flex flex-wrap gap-2">
                            {parsedEntities.entities.survey_numbers.map((s, i) => (
                              <Badge key={i} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                {s.value}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Document References */}
                      {parsedEntities.entities?.document_refs?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Documents Referenced</h4>
                          <div className="space-y-1">
                            {parsedEntities.entities.document_refs.map((d, i) => (
                              <div key={i} className="text-sm flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span>{d.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Title Chain */}
                      {parsedEntities.title_chain?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Ownership Chain (Flow of Title)</h4>
                          <div className="space-y-2">
                            {parsedEntities.title_chain.map((hop, i) => (
                              <div key={i} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                                <span className="font-medium text-gray-700">{hop.from_party}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-700">{hop.to_party}</span>
                                <Badge className="bg-blue-100 text-blue-700 text-xs">{hop.transaction_type}</Badge>
                                {hop.document_ref && <span className="text-xs text-gray-500">({hop.document_ref})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Validation Warnings */}
                      {parsedEntities.validation_warnings?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 text-yellow-700">Extraction Notes</h4>
                          <div className="space-y-1">
                            {parsedEntities.validation_warnings.map((w, i) => (
                              <div key={i} className="text-sm flex items-start space-x-2 text-yellow-700">
                                <AlertTriangle className="w-4 h-4 mt-0.5" />
                                <span>{typeof w === 'string' ? w : (w.msg || w.message || JSON.stringify(w))}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Validation Result */}
                {selectedCase.validation_result && (
                  <>
                    {/* Issue Counts */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Issue Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-red-50 rounded-lg text-center">
                            <p className="text-3xl font-bold text-red-600">{selectedCase.validation_result.issue_counts?.critical || 0}</p>
                            <p className="text-sm text-red-700">Critical Issues</p>
                          </div>
                          <div className="p-4 bg-yellow-50 rounded-lg text-center">
                            <p className="text-3xl font-bold text-yellow-600">{selectedCase.validation_result.issue_counts?.medium || 0}</p>
                            <p className="text-sm text-yellow-700">Medium Issues</p>
                          </div>
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <p className="text-3xl font-bold text-blue-600">{selectedCase.validation_result.issue_counts?.minor || 0}</p>
                            <p className="text-sm text-blue-700">Minor Issues</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Dimension Scores (Phase 3) */}
                    {selectedCase.validation_result.dimension_scores && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <BarChart3 className="w-5 h-5 text-emerald-600" />
                            <span>Validation Dimensions</span>
                          </CardTitle>
                          <CardDescription>Weighted scoring across 7 validation dimensions</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {Object.entries(selectedCase.validation_result.dimension_scores).map(([key, dim]) => (
                              <div key={key} className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                                  <span className="text-sm text-gray-500">{dim.score}/100 ({(dim.weight * 100).toFixed(0)}% weight)</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      dim.score >= 80 ? 'bg-green-500' :
                                      dim.score >= 60 ? 'bg-yellow-500' :
                                      dim.score >= 40 ? 'bg-orange-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${dim.score}%` }}
                                  />
                                </div>
                                {dim.explanation && (
                                  <p className="text-xs text-gray-500">{dim.explanation}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {/* Blocking Issues */}
                          {selectedCase.validation_result.blocking_issues?.length > 0 && (
                            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                              <h4 className="font-medium text-red-700 mb-2 flex items-center">
                                <XCircle className="w-4 h-4 mr-2" />
                                Blocking Issues
                              </h4>
                              <ul className="space-y-1">
                                {selectedCase.validation_result.blocking_issues.map((issue, idx) => (
                                  <li key={idx} className="text-sm text-red-600">• {typeof issue === 'string' ? issue : (issue.msg || JSON.stringify(issue))}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Conditional Items */}
                          {selectedCase.validation_result.conditional_items?.length > 0 && (
                            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                              <h4 className="font-medium text-yellow-700 mb-2 flex items-center">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Conditions to Meet
                              </h4>
                              <ul className="space-y-1">
                                {selectedCase.validation_result.conditional_items.map((item, idx) => (
                                  <li key={idx} className="text-sm text-yellow-700">• {typeof item === 'string' ? item : (item.msg || JSON.stringify(item))}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Compliance Checks */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Compliance Checks</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Structural */}
                        <div>
                          <h4 className="font-medium mb-2">Structural Compliance</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedCase.validation_result.structural_compliance?.map((check, idx) => (
                              <div key={idx} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                                {check.status === 'pass' ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span className="text-sm">{check.section}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* RBI Compliance */}
                        <div>
                          <h4 className="font-medium mb-2">RBI Compliance</h4>
                          <div className="space-y-2">
                            {selectedCase.validation_result.rbi_compliance?.map((check, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                <div className="flex items-center space-x-2">
                                  {check.status === 'pass' ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : check.status === 'partial' ? (
                                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  )}
                                  <span className="font-medium">{check.rule_name}</span>
                                </div>
                                <span className="text-sm text-gray-500">{check.observation}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Bank Rules */}
                        <div>
                          <h4 className="font-medium mb-2">Bank-Specific Rules</h4>
                          <div className="space-y-2">
                            {selectedCase.validation_result.bank_rules_compliance?.map((check, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                <div className="flex items-center space-x-2">
                                  {check.status === 'pass' ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : check.status === 'partial' ? (
                                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  )}
                                  <span className="font-medium">{check.rule_name}</span>
                                </div>
                                <div className="text-right text-sm">
                                  <span className="text-gray-500">Expected: {check.expected}</span>
                                  <span className="mx-2">|</span>
                                  <span className={check.status === 'pass' ? 'text-green-600' : 'text-red-600'}>Found: {check.found}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Human Decision Actions */}
                    {selectedCase.status === 'validated' && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Human Decision</CardTitle>
                          <CardDescription>Review the validation results and make a decision</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex space-x-4">
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                const remarks = prompt('Enter remarks for approval:');
                                if (remarks) handleMarkFit(selectedCase._id, remarks);
                              }}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Fit for Processing
                            </Button>
                            <Button 
                              variant="outline"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                              onClick={() => {
                                const remarks = prompt('Enter remarks for reversion:');
                                if (remarks) handleRevertToAdvocate(selectedCase._id, remarks);
                              }}
                            >
                              <ArrowLeft className="w-4 h-4 mr-2" />
                              Revert to Advocate
                            </Button>
                            <Button 
                              variant="outline"
                              className="border-red-500 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                const remarks = prompt('Enter remarks for rejection:');
                                if (remarks) handleMarkNotFit(selectedCase._id, remarks);
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Mark Not Fit
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Audit Trail */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Audit Trail</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedCase.audit_trail?.map((entry, idx) => (
                            <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded">
                              <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div>
                                <p className="font-medium text-gray-900">{entry.action}</p>
                                <p className="text-sm text-gray-500">{entry.remarks}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(entry.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            ) : (
              /* Cases List */
              <Card>
                <CardHeader>
                  <CardTitle>All Validation Cases</CardTitle>
                </CardHeader>
                <CardContent>
                  {cases.length === 0 ? (
                    <div className="text-center py-12">
                      <Scale className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No validation cases yet</p>
                      <Button 
                        className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setActiveTab('new-validation')}
                      >
                        Start First Validation
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cases.map(c => (
                        <div 
                          key={c._id} 
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleViewCase(c._id)}
                          data-testid={`love-case-${c._id}`}
                        >
                          <div className="flex items-center space-x-4">
                            <Building2 className="w-8 h-8 text-gray-400" />
                            <div>
                              <p className="font-semibold text-gray-900">{c.case_number}</p>
                              <p className="text-sm text-gray-500">{c.bank_name} • {c.branch}</p>
                              <p className="text-sm text-gray-500">Applicants: {c.applicant_names?.join(', ')}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              {c.validation_score && (
                                <p className="text-xl font-bold text-gray-700">{c.validation_score}/100</p>
                              )}
                              <p className="text-xs text-gray-400">{c.documents_count} docs</p>
                            </div>
                            {getStatusBadge(c.status)}
                            {c.recommendation && getRecommendationBadge(c.recommendation)}
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Disclaimer Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-500">
          <p className="font-medium text-gray-700 mb-1">DISCLAIMER</p>
          <p>
            This is a quality and compliance validation tool. It does NOT replace the advocate's legal opinion. 
            All recommendations are system-generated and subject to human review. 
            Final decisions rest with the reviewing officer.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LegalOpinionValidation;
